import type { DealProductBuilderRow, FixedRateLoanDealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import { fixedRateLoanFieldDefinitions } from '@src/utils/OTC/fixedRateLoanFieldDefinitions';
import { getColumnTitle, getColumnTooltip } from '@src/utils/fieldDefinitionUtils';
import type { useDisclosureWithContext } from '@talos/kyoko';
import { useMemo } from 'react';
import { useDealProductBuilderAssets } from '../DealProductBuilderAssetsProvider';
import type { DealProductBuilderColumn } from '../dealProductBuilderColDef';
import { DealProductBuilderGrid } from './DealProductBuilderGrid';
import { buildCurrencySelectorColumn } from './baseProductBuilderColumns';

interface FixedRateLoanProductBuilderGridProps {
  panelID: string;
  productDetailsDisclosure: ReturnType<
    typeof useDisclosureWithContext<{ rowIndex: number; rowData: DealProductBuilderRow }>
  >;
}

export const FixedRateLoanProductBuilderGrid = ({
  panelID,
  productDetailsDisclosure,
}: FixedRateLoanProductBuilderGridProps) => {
  const { availableBorrowedAssets, availableCollateralAssets } = useDealProductBuilderAssets();

  const customColumns = useMemo(() => {
    const borrowedAssetColumn = buildCurrencySelectorColumn<FixedRateLoanDealProductBuilderRow>({
      key: 'baseAsset',
      headerName: getColumnTitle(fixedRateLoanFieldDefinitions.borrowedAsset),
      currenciesToInclude: availableBorrowedAssets,
      description: getColumnTooltip(fixedRateLoanFieldDefinitions.borrowedAsset) ?? '',
    });
    const collateralAssetColumn = buildCurrencySelectorColumn<FixedRateLoanDealProductBuilderRow>({
      key: 'collateralAsset',
      headerName: getColumnTitle(fixedRateLoanFieldDefinitions.collateralAsset),
      currenciesToInclude: availableCollateralAssets,
      description: getColumnTooltip(fixedRateLoanFieldDefinitions.collateralAsset) ?? '',
    });
    return [borrowedAssetColumn, collateralAssetColumn] as DealProductBuilderColumn[];
  }, [availableBorrowedAssets, availableCollateralAssets]);

  return (
    <DealProductBuilderGrid
      panelID={panelID}
      productDetailsDisclosure={productDetailsDisclosure}
      customColumnDefs={customColumns}
      fieldDefinitions={fixedRateLoanFieldDefinitions}
    />
  );
};
